import { useState, useCallback, useEffect } from 'react';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  limit, 
  startAfter, 
  getDocs 
} from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * Custom hook for paginated data fetching with caching
 * Optimized for large datasets in coaching admin
 */
const usePaginatedData = (collectionName, filters = {}, pageSize = 20, orderByField = 'createdAt', orderDirection = 'desc') => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [lastDoc, setLastDoc] = useState(null);
  const [page, setPage] = useState(1);

  // Cache for already loaded data
  const [cache, setCache] = useState(new Map());

  const loadData = useCallback(async (isLoadMore = false) => {
    if (loading) return;
    
    // OPTIMIZATION: Check cache first (5 minute TTL)
    const cacheKey = `${collectionName}_${JSON.stringify(filters)}_${page}`;
    const cached = localStorage.getItem(cacheKey);
    const cacheTs = localStorage.getItem(`${cacheKey}_ts`);
    
    if (!isLoadMore && cached && cacheTs && (Date.now() - parseInt(cacheTs)) < 300000) {
      const cachedData = JSON.parse(cached);
      setData(cachedData.data);
      setHasMore(cachedData.hasMore);
      setLastDoc(cachedData.lastDoc);
      return;
    }
    
    setLoading(true);
    setError(null);

    try {
      // Build query
      let q = collection(db, collectionName);
      
      // Apply filters
      const filterArray = Object.entries(filters).filter(([_, value]) => value !== undefined && value !== null);
      if (filterArray.length > 0) {
        filterArray.forEach(([key, value]) => {
          q = query(q, where(key, '==', value));
        });
      }

      // Apply ordering (only if orderByField is provided)
      if (orderByField) {
        q = query(q, orderBy(orderByField, orderDirection));
      }

      // Apply pagination
      if (isLoadMore && lastDoc) {
        q = query(q, startAfter(lastDoc));
      }

      q = query(q, limit(pageSize));

      // Execute query
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        setHasMore(false);
        if (!isLoadMore) {
          setData([]);
        }
        return;
      }

      const newData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Update last document for pagination
      const newLastDoc = snapshot.docs[snapshot.docs.length - 1];
      setLastDoc(newLastDoc);

      // Update data
      let finalData;
      if (isLoadMore) {
        finalData = [...data, ...newData];
        setData(finalData);
        setPage(prev => prev + 1);
      } else {
        finalData = newData;
        setData(newData);
        setPage(1);
      }

      // Check if there's more data
      const hasMoreData = snapshot.docs.length === pageSize;
      setHasMore(hasMoreData);

      // OPTIMIZATION: Cache the results
      if (!isLoadMore) {
        localStorage.setItem(cacheKey, JSON.stringify({
          data: finalData,
          hasMore: hasMoreData,
          lastDoc: newLastDoc
        }));
        localStorage.setItem(`${cacheKey}_ts`, Date.now().toString());
      }

    } catch (err) {
      console.error('Error loading paginated data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [collectionName, filters, pageSize, orderByField, orderDirection, lastDoc, loading, page, data]);

  const loadMore = useCallback(() => {
    if (hasMore && !loading) {
      loadData(true);
    }
  }, [hasMore, loading, loadData]);

  const refresh = useCallback(() => {
    setLastDoc(null);
    setPage(1);
    setHasMore(true);
    setCache(new Map());
    loadData(false);
  }, [loadData]);

  // Initial load
  useEffect(() => {
    loadData(false);
  }, [collectionName, JSON.stringify(filters)]);

  return {
    data,
    loading,
    error,
    hasMore,
    page,
    loadMore,
    refresh
  };
};

export default usePaginatedData;
